import { type LocationDescriptorObject } from 'history';
import { createSelector } from 'reselect';

import { parseUrlSearchParams } from '~/utils/url';

import { type ApplicationState } from '../store';

const getRouterStore = (state: ApplicationState) => state.router;

export const getLocation = createSelector(
  getRouterStore,
  (routerStore): LocationDescriptorObject => routerStore!.location,
);

export const getQS = createSelector(getLocation, (location) => location.search);

export const getParsedQS = createSelector(getQS, (querystring) =>
  parseUrlSearchParams(querystring!.substring(1)),
);

export const getPathname = createSelector(getLocation, (location): string => location!.pathname!);

const getQSOrderStatus = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.order_status as string,
);

const getQSSortBy = createSelector(
  getParsedQS,
  (queryParams): string => (queryParams.sort_by as string) || '',
);

const getQSOrderBy = createSelector(
  getParsedQS,
  (queryParams): string => (queryParams.order_by as string) || '',
);

const getQSWeightUnit = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.weight_unit as string,
);

const getQSWeightLt = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.weight_lt as string,
);

const getQSWeightGt = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.weight_gt as string,
);

const getQSShippingMethod = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.shipping_method as string,
);

const getQSAddressFrom = createSelector(getParsedQS, (queryParams): string[] | undefined =>
  sortedQSitems(queryParams.address_from as Record<number, string> | string[] | undefined),
);

export const sortedQSitems = (
  items?: Record<number, string> | string | string[],
): string[] | undefined => {
  if (!items) return;

  if (typeof items === 'string') return [items];

  if (Array.isArray(items)) return items.sort();

  return Object.values(items).sort();
};

const getQSItem = createSelector(getParsedQS, (queryParams): string[] | undefined =>
  sortedQSitems(queryParams.item as Record<number, string> | string[] | undefined),
);

const getQSIsInternational = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.is_international as string,
);

const getQSSource = createSelector(getParsedQS, (queryParams): string[] | undefined =>
  sortedQSitems(queryParams.source as Record<number, string> | string[] | undefined),
);

const getQSShopApp = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.shop_app as string,
);

const getQSStartDate = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.start_date as string,
);

const getQSEndDate = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.end_date as string,
);

const aggregateQStags = (
  tags?: Record<number, string> | string | string[],
): string[] | undefined => {
  if (!tags) return;

  if (typeof tags === 'string') return [tags];

  if (Array.isArray(tags)) return tags;

  return Object.values(tags);
};

const getQSTag = createSelector(getParsedQS, (queryParams): string[] | undefined =>
  aggregateQStags(queryParams.tag as Record<number, string> | string[] | undefined),
);

const getQSTotalItemQuantityMax = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.total_item_quantity_max as string,
);

const getQSTotalItemQuantityMin = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.total_item_quantity_min as string,
);

const getQSUntagged = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.untagged as string,
);

export const getQSSaved = createSelector(
  getParsedQS,
  (queryParams): string => queryParams.saved as string,
);

const getQSQ = createSelector(getParsedQS, (queryParams): string => queryParams.q as string);

export const getOrderFiltersValues = createSelector(
  getQSIsInternational,
  getQSShippingMethod,
  getQSAddressFrom,
  getQSSource,
  getQSShopApp,
  getQSStartDate,
  getQSEndDate,
  getQSWeightUnit,
  getQSWeightLt,
  getQSWeightGt,
  getQSOrderStatus,
  getQSQ,
  getQSSortBy,
  getQSOrderBy,
  getQSItem,
  getQSTotalItemQuantityMax,
  getQSTotalItemQuantityMin,
  getQSTag,
  getQSUntagged,
  (
    isInternational,
    shippingMethod,
    addressFrom,
    source,
    shopApp,
    startDate,
    endDate,
    weightUnit,
    weightLt,
    weightGt,
    orderStatus,
    q,
    SortBy,
    OrderBy,
    item,
    totalItemQuantityMax,
    totalItemQuantityMin,
    tag,
    untagged,
  ) => ({
    address_from: addressFrom,
    end_date: endDate,
    is_international: isInternational,
    item,
    order_by: OrderBy,
    order_status: orderStatus,
    q,
    shipping_method: shippingMethod,
    shop_app: shopApp,
    sort_by: SortBy,
    source,
    start_date: startDate,
    tag,
    total_item_quantity_max: totalItemQuantityMax,
    total_item_quantity_min: totalItemQuantityMin,
    untagged,
    weight_gt: weightGt,
    weight_lt: weightLt,
    weight_unit: weightUnit,
  }),
);

export const getNotEmptyOrdersFilters = createSelector(
  getOrderFiltersValues,
  (ordersFiltersValues) =>
    Object.keys(ordersFiltersValues).filter(
      (key) => ordersFiltersValues[key as keyof ReturnType<typeof getOrderFiltersValues>],
    ),
);
